// What the town's trades charge, and what the merchant has on the shelves.
//
// The temple's price follows the injury: healing is sold by the hit point, so
// a scratch costs coppers and a near-corpse costs a fortune. Bringing the dead
// back is a levy on top, heavier the higher the character's level.

import { generateItem, createPotion, itemName } from './items';
import { statBonus } from './combat/rules';

export const HEAL_PER_HP = 3;           // gold for each hit point restored
export const CURE_UNCONSCIOUS = 25;     // and a flat fee for rousing the fallen
export const REVIVE_BASE = 150;         // raising the dead: this, plus a levy
export const REVIVE_PER_LEVEL = 100;    // per level of the character raised
export const SELL_SHARE = 0.4;          // what a merchant pays for used gear

// How many hit points this character is down
export function pointsNeeded(character) {
    return Math.max(0, character.maxHp - Math.max(0, character.hp));
}

// What the temple asks to put this character right, or 0 if they are well
export function healCost(character) {
    if (character.condition === 'dead') return reviveCost(character);
    const points = pointsNeeded(character);
    if (!points && character.condition === 'good') return 0;
    return points * HEAL_PER_HP + (character.condition === 'unconscious' ? CURE_UNCONSCIOUS : 0);
}

// Raising the dead: a levy for the rite, then the healing they still need
export function reviveCost(character) {
    return REVIVE_BASE + character.level * REVIVE_PER_LEVEL + pointsNeeded(character) * HEAL_PER_HP;
}

// The character as the temple hands them back: whole, awake and alive
export function healed(character) {
    return { ...character, hp: character.maxHp, condition: 'good' };
}

// Why the temple can do nothing for this character, or null if it can
export function templeBlocker(character, gold) {
    const cost = healCost(character);
    if (!cost) return `${character.name} is in good health.`;
    if (gold < cost) return `That rite costs ${cost} gold.`;
    return null;
}

// ---------------------------------------------------------------- tavern

// A night's lodging for the whole party. Out in the dungeon they camp for
// nothing; in town the streets are no place to sleep, so a room is the price
// of a full night's rest.
export const ROOM_PRICE = 20;

// What the landlord is saying tonight, for colour
export const RUMOURS = [
    'A drunk swears the deepest floors are older than the town above them.',
    'The landlord says a party went down last month and came back with nothing but a key.',
    'Someone at the next table is selling a map. Nobody is buying.',
    'Two of the guard argue about what has been heard under the floor at night.',
    'A pedlar warns that locked doors below are cheaper to bash than to unpick.',
    'An old woman says the dead come back easier than the living come home.',
];

export function roomBlocker(gold) {
    return gold < ROOM_PRICE ? `A room for the night costs ${ROOM_PRICE} gold.` : null;
}

// ---------------------------------------------------------------- merchant

const STOCK_SIZE = 8;
// The merchant's shelves are stocked for adventurers just setting out
const STOCK_DEPTH = 2;

// Whoever does the talking sets the price. Personality's stat bonus runs from
// -5 for a boor to +18 for someone the whole town likes; each point moves the
// price by two per cent, and no amount of charm moves it past a quarter.
export const HAGGLE_PER_POINT = 0.02;
export const HAGGLE_LIMIT = 0.25;

export function haggle(character) {
    if (!character) return 1;
    const bonus = statBonus(character.stats.personality);
    return Math.min(1 + HAGGLE_LIMIT, Math.max(1 - HAGGLE_LIMIT, 1 - bonus * HAGGLE_PER_POINT));
}

// What this character is asked for an item on the shelves
export function buyPrice(item, character) {
    return Math.max(1, Math.round(item.value * haggle(character)));
}

// ...and what the merchant will pay them for something the party is done with.
// Charm cuts what they pay and lifts what they offer, so it works both ways.
export function sellPrice(item, character) {
    return Math.max(1, Math.round((item.value * SELL_SHARE) / haggle(character)));
}

// The goods in the shop at the start of a run: a few potions and some gear
export function createStock(rng = Math.random) {
    const stock = [createPotion('Healing Potion'), createPotion('Mana Potion')];
    while (stock.length < STOCK_SIZE) {
        stock.push(generateItem(STOCK_DEPTH, rng));
    }
    return stock;
}

// Why this character cannot buy this, or null if they can
export function marketBlocker(item, gold, character) {
    const price = buyPrice(item, character);
    if (gold < price) return `${itemName(item)} costs ${price} gold.`;
    return null;
}
